/**
 * REST client for the Baccarat backend.
 * Ported from
 *   packages/amgbaccarat_v2/lib/data/repositories/baccarat_api_service.dart.
 *
 * Quirks vs Dragon Tiger:
 *   - getInfo uses `gameSessionId = gameKey` (literally the game key string),
 *     NOT the gsid GUID. Comment in baccarat_api_service.dart:117-125 reads
 *     "OLD PROJECT: session = gameKey!".
 *   - md5 hashes are identical (agentId+gameKey+userName for everything
 *     except getInfo which is just md5(userName)).
 */
import { Endpoints, GAME_KEY_SINGLE } from '../config';
import { generateBalanceMd5, generateInfoMd5 } from './md5';
import type {
  BetLine,
  BetResult,
  BetResultItem,
  GameSession,
  SessionCredentials,
  UserInfo,
} from '../types/api';
import { debugLog, error as logError } from '../util/logger';

export class ApiError extends Error {
  status: number;
  unauthorized: boolean;
  constructor(message: string, status: number, unauthorized = false) {
    super(message);
    this.status = status;
    this.unauthorized = unauthorized;
  }
}

export class Api {
  private creds: SessionCredentials = {
    sessionToken: null,
    gameSessionId: null,
    agentId: '',
    userName: '',
  };

  setCredentials(c: SessionCredentials): void {
    this.creds = { ...c };
  }

  getCredentials(): Readonly<SessionCredentials> {
    return this.creds;
  }

  private get headers(): Record<string, string> {
    const h: Record<string, string> = { 'Content-Type': 'application/json' };
    if (this.creds.sessionToken) {
      h.Authorization = `Bearer ${this.creds.sessionToken}`;
    }
    return h;
  }

  private async post(url: string, body: Record<string, unknown>): Promise<unknown> {
    debugLog('[API][REQUEST] POST', url, body);
    let resp: Response;
    try {
      resp = await fetch(url, {
        method: 'POST',
        headers: this.headers,
        body: JSON.stringify(body),
      });
    } catch (e) {
      logError('[API][NETWORK]', e);
      throw new ApiError(String(e), 0);
    }
    const text = await resp.text();
    debugLog('[API][RESPONSE]', url, resp.status, text);
    if (resp.status === 401) {
      throw new ApiError('Unauthorized', 401, true);
    }
    if (resp.status !== 200) {
      throw new ApiError(text || `HTTP ${resp.status}`, resp.status);
    }
    try {
      return JSON.parse(text);
    } catch (e) {
      throw new ApiError(`Invalid JSON: ${String(e)}`, resp.status);
    }
  }

  private unwrap(json: unknown): Record<string, unknown> {
    if (!json || typeof json !== 'object') {
      throw new ApiError('Invalid response body: expected object', 200);
    }
    const obj = json as Record<string, unknown>;

    if ('ErrorCode' in obj) {
      const code = Number(obj.ErrorCode ?? 0);
      if (code !== 0) {
        throw new ApiError(
          (obj.Message as string) ?? `ErrorCode ${code}`,
          200,
        );
      }
      return this.extractData(obj);
    }

    if (obj.success === false || obj.error != null) {
      throw new ApiError(
        (obj.message as string) ?? (obj.error as string) ?? 'Unknown error',
        200,
      );
    }
    return this.extractData(obj);
  }

  private extractData(json: Record<string, unknown>): Record<string, unknown> {
    const field = json.data ?? json.Data;
    if (field && typeof field === 'object' && !Array.isArray(field)) {
      return field as Record<string, unknown>;
    }
    if (Array.isArray(field) && field.length > 0 && typeof field[0] === 'object') {
      return field[0] as Record<string, unknown>;
    }
    return json;
  }

  async login(opts: {
    userName: string;
    agentId: string;
    password: string;
  }): Promise<{ sessionId: string; balance: number; agentId: string; userName: string }> {
    const body: Record<string, unknown> = {
      userName: opts.userName,
      agentId: opts.agentId,
      password: opts.password,
    };
    const raw = await this.post(Endpoints.login, body);
    const data = this.unwrap(raw);
    return {
      sessionId: str(pick(data, 'SessionId', 'sessionId')),
      balance: num(pick(data, 'Balance', 'balance')),
      agentId: str(pick(data, 'AgentId', 'agentId')),
      userName: str(pick(data, 'UserName', 'userName')),
    };
  }

  async openGame(opts: {
    userName: string;
    agentId: string;
    gameKey?: string;
  }): Promise<GameSession> {
    const gameKey = opts.gameKey ?? GAME_KEY_SINGLE;
    const md5 = generateBalanceMd5(opts.agentId, gameKey, opts.userName);
    const body: Record<string, unknown> = {
      userName: opts.userName,
      agentId: opts.agentId,
      token: 'token',
      gameKey,
      md5,
    };
    const raw = await this.post(Endpoints.openGame, body);
    const data = this.unwrap(raw);
    return parseGameSession(data);
  }

  async checkBalance(opts: {
    sessionId: string;
    gameKey?: string;
  }): Promise<number> {
    const gameKey = opts.gameKey ?? GAME_KEY_SINGLE;
    if (!this.creds.gameSessionId) this.creds.gameSessionId = opts.sessionId;
    const md5 = generateBalanceMd5(
      this.creds.agentId,
      gameKey,
      this.creds.userName,
    );
    const body: Record<string, unknown> = {
      userName: this.creds.userName,
      agentId: this.creds.agentId,
      gameKey,
      md5,
    };
    if (this.creds.sessionToken) body.token = this.creds.sessionToken;
    const raw = await this.post(Endpoints.checkBalance, body);
    const data = this.unwrap(raw);

    const nextToken = (data.sessionId ?? data.SessionId) as string | undefined;
    if (nextToken) this.creds.sessionToken = nextToken;
    const nextAgent = (data.agentId ?? data.AgentId) as string | undefined;
    if (nextAgent) this.creds.agentId = nextAgent;

    const balance = (data.balance ?? data.Balance) as number | undefined;
    return Number(balance ?? 0);
  }

  /**
   * getInfo — returns Chips / MinBet / MaxBet for the table. `gameSessionId`
   * must be the gsid GUID, NOT the game key: the Flutter repository calls this
   * with `gameSessionId: player.gsid` (game_repository.dart:102) and only a
   * legacy project used the game key (the misleading "session = gameKey" comment
   * in baccarat_api_service.dart). Sending "BKA1" here made the backend return
   * no config in single-player, so the chip bar / min-max stayed on defaults.
   * Mirrors amg_dragontiger_pixi.getInfo.
   */
  async getInfo(opts: { sessionId: string }): Promise<UserInfo> {
    if (!this.creds.gameSessionId) this.creds.gameSessionId = opts.sessionId;
    const md5 = generateInfoMd5(this.creds.userName);
    const body: Record<string, unknown> = {
      userName: this.creds.userName,
      gameKey: GAME_KEY_SINGLE,
      md5,
    };
    if (this.creds.gameSessionId) body.gameSessionId = this.creds.gameSessionId;
    const raw = await this.post(Endpoints.getInfo, body);
    const data = this.unwrap(raw);
    return parseUserInfo(data);
  }

  async placeBet(opts: {
    sessionId: string;
    bets: BetLine[];
    gid?: string;
    gameKey?: string;
  }): Promise<BetResult> {
    const gameKey = opts.gameKey ?? GAME_KEY_SINGLE;
    const md5 = generateBalanceMd5(
      this.creds.agentId,
      gameKey,
      this.creds.userName,
    );
    const body: Record<string, unknown> = {
      userName: this.creds.userName,
      agentId: this.creds.agentId,
      token: 'string', // Flutter sends literal dummy here; real auth is Bearer
      gameKey,
      md5,
      betDetail: opts.bets,
      gid: opts.gid ?? opts.sessionId,
    };
    const raw = await this.post(Endpoints.placeBet, body);
    const data = this.unwrap(raw);
    return parseBetResult(data);
  }
}

// =====================================================================
// Wire-format parsers (PascalCase + camelCase both supported)
// =====================================================================

function num(v: unknown, dflt = 0): number {
  if (typeof v === 'number') return v;
  if (typeof v === 'string' && v !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : dflt;
  }
  return dflt;
}
function str(v: unknown, dflt = ''): string {
  return typeof v === 'string' ? v : dflt;
}
function bool(v: unknown, dflt = false): boolean {
  return typeof v === 'boolean' ? v : dflt;
}
function pick<T = unknown>(o: Record<string, unknown>, ...keys: string[]): T | undefined {
  for (const k of keys) {
    if (o[k] !== undefined && o[k] !== null) return o[k] as T;
  }
  return undefined;
}

function parseGameSession(d: Record<string, unknown>): GameSession {
  return {
    balance: num(pick(d, 'Balance', 'balance')),
    currentGameUrl: str(pick(d, 'CurrentGameUrl', 'currentGameUrl')),
    agentId: str(pick(d, 'AgentId', 'agentId')),
    sessionId: str(pick(d, 'SessionId', 'sessionId')),
    gameName: str(pick(d, 'GameName', 'gameName')),
    nextRoundId: str(pick(d, 'NextRoundId', 'nextRoundId')),
    ...parseGameConfig(d),
  };
}

function parseUserInfo(d: Record<string, unknown>): UserInfo {
  return {
    balance: num(pick(d, 'Balance', 'balance')),
    agentId: str(pick(d, 'AgentId', 'agentId')),
    userName: str(pick(d, 'UserName', 'userName')),
    sessionId: str(pick(d, 'SessionId', 'sessionId')),
    gameKey: str(pick(d, 'GameKey', 'gameKey')),
    gameName: str(pick(d, 'GameName', 'gameName')),
    isActive: bool(pick(d, 'IsActive', 'isActive'), true),
    isLock: bool(pick(d, 'IsLock', 'isLock')),
    avatarId: num(pick(d, 'AvatarId', 'avatarId')),
    ...parseGameConfig(d),
  };
}

function parseGameConfig(d: Record<string, unknown>): {
  chips: number[];
  minBet: number;
  maxBet: number;
} {
  const raw = pick<unknown>(d, 'Chips', 'chips');
  const chips: number[] = [];
  if (typeof raw === 'string') {
    for (const piece of raw.split(',')) {
      const n = parseInt(piece.trim(), 10);
      if (Number.isFinite(n) && n > 0) chips.push(n);
    }
  } else if (Array.isArray(raw)) {
    for (const v of raw) {
      const n = typeof v === 'number' ? v : parseInt(String(v), 10);
      if (Number.isFinite(n) && n > 0) chips.push(n);
    }
  }
  return {
    chips,
    minBet: num(pick(d, 'MinBet', 'minBet')),
    maxBet: num(pick(d, 'MaxBet', 'maxBet')),
  };
}

function parseBetResultItem(d: Record<string, unknown>): BetResultItem {
  const winLose = num(pick(d, 'winLose', 'WinLose'));
  const rawIsWin = pick<unknown>(d, 'isWin');
  return {
    betItemId: num(pick(d, 'betItemId', 'BetIemId', 'BetItemId')),
    betAmount: num(pick(d, 'betAmount', 'Amount', 'amount')),
    winLose,
    isWin: typeof rawIsWin === 'boolean' ? rawIsWin : winLose > 0,
  };
}

function parseBetResult(d: Record<string, unknown>): BetResult {
  const arrRaw =
    pick<unknown[]>(d, 'BetResults', 'betResults', 'betResult') ?? [];
  const betResults = (arrRaw as unknown[])
    .filter((x): x is Record<string, unknown> => !!x && typeof x === 'object')
    .map(parseBetResultItem);

  const totalWinLose = num(pick(d, 'TotalWinLose', 'totalWinLose'));
  let positive = 0;
  let hasPositive = false;
  for (const r of betResults) {
    if (r.winLose > 0) {
      positive += r.winLose;
      hasPositive = true;
    }
  }

  let winMode: 0 | 1 | 2 = 0;
  if (totalWinLose > 0) winMode = 2;
  else if (totalWinLose < 0) winMode = 1;

  return {
    result: str(pick(d, 'Result', 'result')),
    totalWinLose,
    afterBalance: num(pick(d, 'Balance', 'afterBalance', 'balance')),
    nextRoundId: str(pick(d, 'NextRoundId', 'nextRoundId')),
    betResults,
    winMode,
    positiveWinAmount: positive,
    hasPositiveWin: hasPositive,
  };
}
